//#region Imports

import { NgClass } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { TabsModule } from 'primeng/tabs';
import { openSuccessModal } from '../../../helpers/modal.helper';
import { hasVisibleRichTextContent } from '../../../shared/helpers/rich-text.helper';
import { ButtonComponent } from '../../../shared/components/button/button.component';
import { ButtonRadiusEnum } from '../../../shared/components/button/types/button-radius.enum';
import { ButtonSizeEnum } from '../../../shared/components/button/types/button-size.enum';
import { ButtonTypeEnum } from '../../../shared/components/button/types/button-type.enum';
import { CourseAboutStepComponent } from '../../../shared/components/course-about-step/course-about-step.component';
import {
  CourseContentStepComponent,
  SelectedCourseCard,
} from '../../../shared/components/course-content-step/course-content-step.component';
import { CourseModulesStepComponent } from '../../../shared/components/course-modules-step/course-modules-step.component';
import { CourseSummaryStepComponent } from '../../../shared/components/course-summary-step/course-summary-step.component';
import { SpinnerComponent } from '../../../shared/components/spinner/spinner.component';
import { SpinnerColorEnum } from '../../../shared/components/spinner/types/spinner-color.enum';
import { SpinnerSizeEnum } from '../../../shared/components/spinner/types/spinner-size.enum';
import { ContentType } from '../../../shared/models/enums/content-type.enum';
import { Course } from '../../../shared/models/interfaces/course.interface';
import { CourseCreateStepEnum } from '../../../shared/models/enums/course-create-step.enum';
import { CourseStatus } from '../../../shared/models/enums/course-status.enum';
import { Class, ClassPayload, Content, ContentPayload } from '../../../shared/models/interfaces/course-content.interface';
import { Module } from '../../../shared/models/interfaces/module.interface';
import { ModalService } from '../../../shared/services/modal/modal.service';
import { CourseService } from '../../../shared/services/course/course.service';
import { UploadService } from '../../../shared/services/upload/upload.service';
import { CategoryService } from '../../../shared/services/category/category.service';
import { ToastService } from '../../../shared/components/toast/service/toast.service';
import { CoursePayload, CourseModuleWritePayload, CourseContentWritePayload } from '../../../shared/types/payloads/course.payload';
import { getCrudErrorMessage } from '../../../shared/utils/functions';
import { minutesToDurationHoursInput, toClassWritePayload, toCourseApiPayload, toCourseRelatedResources, collectPersistedContentIds, groupCourseContents, fromCourseApiStatus } from '../../../shared/utils/course-write.mapper';
import { CourseType } from '../../../shared/models/enums/course-type.enum';
import { CourseLocation } from '../../../shared/models/enums/course-location.enum';
import {
  COURSE_CONCLUSION_CONTENT,
  CourseConclusionTypesEnum,
  getCourseCreateTabSteps,
} from '../constants/course-create.constants';
import { Tag } from '../../../shared/models/interfaces/tag.interface';
import { Category } from '../../../shared/models/interfaces/category.interface';
import { TabStep } from '../../../shared/models/interfaces/tab-step.interface';
import {
  ClassContentUploadState,
  ClassForm,
  ClassFormValue,
  CourseAboutForm,
  CourseCreateForm,
  EvaluationForm,
  EvaluationOptionForm,
  EvaluationQuestionForm,
  ModuleForm,
} from '../../../shared/models/interfaces/forms/course-form.interface';
import { CourseContentFormComponent } from '../components/course-content-form/course-content-form.component';
import {
  Evaluation,
  EvaluationOption,
  EvaluationQuestion,
} from '../../../shared/models/interfaces/course-evaluation.interface';
import { CourseEvaluationFormComponent } from '../../../shared/components/course-evaluation-form/course-evaluation-form.component';

//#endregion

@Component({
  selector: 'app-course-create',
  standalone: true,
  templateUrl: './course-create.component.html',
  host: {
    class: 'flex flex-1 flex-col w-full',
  },
  imports: [
    NgClass,
    ReactiveFormsModule,
    TabsModule,
    ButtonComponent,
    CourseAboutStepComponent,
    CourseModulesStepComponent,
    CourseContentStepComponent,
    CourseSummaryStepComponent,
    CourseContentFormComponent,
    SpinnerComponent,
    CourseEvaluationFormComponent
  ],
})
export class CourseCreateComponent implements OnInit {

  //#region Constructor

  constructor(
    private readonly router: Router,
    private readonly route: ActivatedRoute,
    private readonly modalService: ModalService,
    private readonly courseService: CourseService,
    private readonly categoryService: CategoryService,
    private readonly uploadService: UploadService,
    private readonly toastService: ToastService,
    private readonly formBuilder: FormBuilder,
  ) {
    this.formGroup = this.formBuilder.group<CourseCreateForm>({
      about: this.formBuilder.group<CourseAboutForm>({
        title: new FormControl('', { nonNullable: true }),
        categoryId: new FormControl<string | null>(null, { nonNullable: true }),
        duration: new FormControl('', { nonNullable: true }),
        type: new FormControl(CourseType.COURSE, { nonNullable: true }),
        location: new FormControl(CourseLocation.ONLINE, { nonNullable: true }),
        coverImageUrl: new FormControl('', { nonNullable: true }),
        tags: new FormControl<Tag[]>([], { nonNullable: true }),
        shortDescription: new FormControl('', { nonNullable: true }),
        detailedDescription: new FormControl('', { nonNullable: true }),
      }),
      modules: this.formBuilder.array<FormGroup<ModuleForm>>([]),
    });
  }

  //#endregion

  //#region Public Properties

  public courseId: string | null = null;

  public isUpdate: boolean = false;

  public courseStatus: CourseStatus | null = null;

  public isLoading: boolean = false;

  public isSaving: boolean = false;

  public isFormReady: boolean = false;

  public loadErrorMessage: string = '';

  public coverImageSrc: string = '';

  public coverImageFile: File | null = null;

  public hasCoverPreview: boolean = false;

  public activeStepId: CourseCreateStepEnum = CourseCreateStepEnum.ABOUT;

  public selectedCard: SelectedCourseCard | null = null;

  public formGroup!: FormGroup<CourseCreateForm>;

  public categories: Category[] = [];

  public readonly buttonTypeEnum = ButtonTypeEnum;

  public readonly buttonRadiusEnum = ButtonRadiusEnum;

  public readonly buttonSizeEnum = ButtonSizeEnum;

  public readonly spinnerColorEnum = SpinnerColorEnum;

  public readonly spinnerSizeEnum = SpinnerSizeEnum;

  public readonly courseCreateStepEnum = CourseCreateStepEnum;

  public readonly courseStatusEnum = CourseStatus;

  //#endregion

  //#region Private Properties

  private readonly contentPanelUploadStates = new Map<string, ClassContentUploadState>();

  private persistedIds = new Set<string>();

  private readonly stepAccessChecks: Record<CourseCreateStepEnum, () => boolean> = {
    [CourseCreateStepEnum.ABOUT]: () => true,
    [CourseCreateStepEnum.MODULES]: () => this.canAccessModulesStep,
    [CourseCreateStepEnum.CONTENT]: () => this.modulesForm.length > 0,
    [CourseCreateStepEnum.SUMMARY]: () => this.canAccessSummaryStep,
  };

  //#endregion

  //#region Getters

  public get aboutForm(): FormGroup<CourseAboutForm> {
    return this.formGroup.controls.about;
  }

  public get modulesForm(): FormArray<FormGroup<ModuleForm>> {
    return this.formGroup.controls.modules;
  }

  public get tabs(): TabStep[] {
    return getCourseCreateTabSteps(
      this.canAccessModulesStep,
      this.modulesForm.length > 0,
      this.canAccessSummaryStep,
    );
  }

  public get canAccessModulesStep(): boolean {
    return !this.isCreateDisabled;
  }

  public get canAccessSummaryStep(): boolean {
    return this.hasFilledModule && this.hasFilledContent;
  }

  public get hasFilledModule(): boolean {
    return this.modulesForm.controls.some((module) => module.controls.name.value.trim().length > 0);
  }

  public get hasFilledContent(): boolean {
    return this.modulesForm.controls.some((module) =>
      module.controls.classes.controls.some(
        (courseClass) =>
          courseClass.controls.name.value.trim().length > 0
          && this.isClassFilled(courseClass.getRawValue()),
      ),
    );
  }

  public get isCreateDisabled(): boolean {
    return !this.hasRequiredAboutFields || this.isSaving;
  }

  public get hasRequiredAboutFields(): boolean {
    const title = this.aboutForm.controls.title.value.trim();
    const categoryId = this.aboutForm.controls.categoryId.value;
    const category = categoryId ? this.categories.find((item) => item.id === categoryId) : null;

    return !!title && !!category && this.hasCoverPreview; 
  }

  public get isCourseComplete(): boolean {
    return this.hasRequiredAboutFields && this.canAccessSummaryStep;
  }

  public get isEditingDraft(): boolean {
    return this.isUpdate && this.courseStatus === CourseStatus.DRAFT;
  }

  public get selectedClassForm(): FormGroup<ClassForm> | null {
    const cardId = this.selectedCard?.type === 'content' ? this.selectedCard.id : null;
    if (!cardId) {
      return null;
    }

    for (const module of this.modulesForm.controls) {
      const classForm = module.controls.classes.controls.find((item) => item.controls.id.value === cardId);
      if (classForm) {
        return classForm;
      }
    }

    return null;
  }

  public get selectedEvaluationForm(): FormGroup<EvaluationForm> | null {
    const cardId = this.selectedCard?.type === 'evaluation' ? this.selectedCard.id : null;
    if (!cardId) {
      return null;
    }

    for (const module of this.modulesForm.controls) {
      const evaluationForm = module.controls.evaluations.controls.find((item) => item.controls.id.value === cardId);
      if (evaluationForm) {
        return evaluationForm;
      }
    }

    return null;
  }

  public get selectedClassContentUploadState(): ClassContentUploadState {
    const classForm = this.selectedClassForm;
    if (!classForm) {
      return {};
    }

    return this.contentPanelUploadStates.get(classForm.controls.id.value) ?? {};
  }

  //#endregion

  //#region Lifecycle Hooks

  public async ngOnInit(): Promise<void> {
    await this.loadCategories();

    const courseId = this.route.snapshot.paramMap.get('courseId');

    if (!courseId) {
      this.isFormReady = true;
      return;
    }

    this.courseId = courseId;
    this.isUpdate = true;
    await this.loadCourse(courseId);
  }

  //#endregion

  //#region Public Methods

  public onCoverPreviewState(hasPreview: boolean): void {
    this.hasCoverPreview = hasPreview;
  }

  public onCoverPreviewSrcChange(src: string): void {
    this.coverImageSrc = src;
  }

  public onCoverFileChange(file: File | null): void {
    this.coverImageFile = file;
  }

  public onModulesFormChange(): void {
    if (this.modulesForm.length === 0) {
      this.selectedCard = null;

      if (this.activeStepId === CourseCreateStepEnum.CONTENT) {
        this.activeStepId = CourseCreateStepEnum.MODULES;
      }
    }

    this.ensureValidActiveStep();
  }

  public async onCreateCourse(): Promise<void> {
    if (this.isCreateDisabled) {
      return;
    }

    const conclusionType: CourseConclusionTypesEnum = this.isUpdate ? CourseConclusionTypesEnum.UPDATED : CourseConclusionTypesEnum.CREATED;
    await this.saveCourse(CourseStatus.ACTIVE, conclusionType);
  }

  public onCancel(): void {
    if (this.isSaving) {
      return;
    }

    void this.router.navigate(['/courses']);
  }

  public onBackToAbout(): void {
    if (this.isSaving) {
      return;
    }

    this.activeStepId = CourseCreateStepEnum.ABOUT;
  }

  public onBackToModules(): void {
    if (this.isSaving) {
      return;
    }

    this.activeStepId = CourseCreateStepEnum.MODULES;
  }

  public onAdvanceToContent(): void {
    if (this.isSaving || this.modulesForm.length === 0) {
      return;
    }

    this.activeStepId = CourseCreateStepEnum.CONTENT;
  }

  public onAdvanceToSummary(): void {
    if (this.isSaving || !this.canAccessSummaryStep) {
      return;
    }

    this.activeStepId = CourseCreateStepEnum.SUMMARY;
  }

  public onBackToContent(): void {
    if (this.isSaving) {
      return;
    }

    this.activeStepId = CourseCreateStepEnum.CONTENT;
  }

  public async onSaveChanges(): Promise<void> {
    const status = this.isEditingDraft ? CourseStatus.DRAFT : CourseStatus.ACTIVE;
    const conclusionType: CourseConclusionTypesEnum = CourseConclusionTypesEnum.UPDATED;
    await this.saveCourse(status, conclusionType);
  }

  public async onPublishCourse(): Promise<void> {
    const conclusionType: CourseConclusionTypesEnum = this.isEditingDraft || !this.isUpdate ? CourseConclusionTypesEnum.PUBLISHED : CourseConclusionTypesEnum.UPDATED;
    await this.saveCourse(CourseStatus.ACTIVE, conclusionType);
  }

  public async onSaveDraft(): Promise<void> {
    const conclusionType: CourseConclusionTypesEnum = CourseConclusionTypesEnum.DRAFT;
    await this.saveCourse(CourseStatus.DRAFT, conclusionType);
  }

  public async onSaveDraftOrChanges(): Promise<void> {
    if (this.isEditingDraft) {
      await this.onSaveChanges();
      return;
    }

    await this.onSaveDraft();
  }

  public onTabStepChange(stepId: string | number): void {
    if (this.isSaving || typeof stepId !== 'string' || !(stepId in this.stepAccessChecks)) {
      return;
    }

    const step = stepId as CourseCreateStepEnum;
    if (this.stepAccessChecks[step]()) {
      this.activeStepId = step;
    }
  }

  public onSelectedContentCardChange(card: SelectedCourseCard | null): void {
    this.selectedCard = card;
  }

  public onAddModule(): void {
    this.modulesForm.push(this.formBuilder.group<ModuleForm>({
      id: new FormControl(crypto.randomUUID(), { nonNullable: true }),
      order: new FormControl(this.modulesForm.length, { nonNullable: true }),
      name: new FormControl('', { nonNullable: true }),
      isActive: new FormControl(true, { nonNullable: true }),
      classes: this.formBuilder.array<FormGroup<ClassForm>>([]),
      evaluations: this.formBuilder.array<FormGroup<EvaluationForm>>([]),
    }));
    this.onModulesFormChange();
  }

  public onAddClass(moduleId: string): void {
    const moduleForm = this.modulesForm.controls.find((item) => item.controls.id.value === moduleId);
    if (!moduleForm) {
      return;
    }

    const order = moduleForm.controls.classes.length;
    moduleForm.controls.classes.push(this.formBuilder.group<ClassForm>({
      id: new FormControl(crypto.randomUUID(), { nonNullable: true }),
      order: new FormControl(order, { nonNullable: true }),
      name: new FormControl('', { nonNullable: true }),
      type: new FormControl(null, { nonNullable: true }),
      videoTitle: new FormControl('', { nonNullable: true }),
      videoThumbnailUrl: new FormControl('', { nonNullable: true }),
      videoFileName: new FormControl('', { nonNullable: true }),
      editorContent: new FormControl('', { nonNullable: true }),
      youtubeUrl: new FormControl('', { nonNullable: true }),
      vimeoUrl: new FormControl('', { nonNullable: true }),
      webPageFileName: new FormControl('', { nonNullable: true }),
    }));
    this.onModulesFormChange();
  }

  public onAddEvaluation(moduleId: string): string {
    const moduleForm = this.modulesForm.controls.find((item) => item.controls.id.value === moduleId);
    if (!moduleForm) {
      return '';
    }

    const order = moduleForm.controls.evaluations.length;
    const evaluationId = crypto.randomUUID();
    moduleForm.controls.evaluations.push(this.formBuilder.group<EvaluationForm>({
      id: new FormControl(evaluationId, { nonNullable: true }),
      order: new FormControl(order, { nonNullable: true }),
      questions: this.formBuilder.array<FormGroup<EvaluationQuestionForm>>([]),
    }));
    this.onModulesFormChange();
    return evaluationId;
  }

  public onEvaluationAdded(moduleId: string): void {
    const evaluationId = this.onAddEvaluation(moduleId);
    if (evaluationId) {
      this.selectedCard = { type: 'evaluation', id: evaluationId };
    }
  }

  public onSelectedClassContentUploadStateChange(uploadState: ClassContentUploadState): void {
    const classForm = this.selectedClassForm;
    if (!classForm) {
      return;
    }

    this.contentPanelUploadStates.set(classForm.controls.id.value, uploadState);
    this.ensureValidActiveStep();
  }

  //#endregion

  //#region Private Methods

  private async loadCategories(): Promise<void> {
    try {
      this.categories = (await this.categoryService.getMany()).data;
    } catch (error) {
      this.toastService.error(getCrudErrorMessage(error), 'Oops...');
    }
  }

  private async loadCourse(courseId: string): Promise<void> {
    this.isLoading = true;
    this.loadErrorMessage = '';
    this.isFormReady = false;

    try {
      const course = await this.courseService.getById(courseId);

      if (!course) {
        throw new Error('Não encontrado.');
      }
      this.applyCourse(course);
      this.isFormReady = true;
    } catch {
      this.loadErrorMessage = 'Não foi possível carregar o curso. Tente novamente.';
    } finally {
      this.isLoading = false;
    }
  }

  private applyCourse(course: Course): void {
    this.courseStatus = fromCourseApiStatus(course.status as string);
    this.persistedIds = collectPersistedContentIds(course);
    this.aboutForm.patchValue({
      title: course.title,
      categoryId: course.category?.id ?? course.categoryId ?? null,
      duration: minutesToDurationHoursInput(course.durationMinutes),
      type: course.type ?? CourseType.COURSE,
      location: course.location ?? CourseLocation.ONLINE,
      coverImageUrl: course.coverImageUrl ?? '',
      tags: course.tags ?? [],
      shortDescription: course.shortDescription ?? '',
      detailedDescription: course.description ?? '',
    });
    this.coverImageSrc = course.coverImageUrl ?? '';
    this.hasCoverPreview = !!course.coverImageUrl?.trim();

    if (!Array.isArray(course.modules)) {
      return;
    }

    const groupedContents = groupCourseContents(course);
    const modulesArray = this.formGroup.controls.modules;
    modulesArray.clear();

    course.modules.forEach((module, index) => {
      const moduleContents = groupedContents.find((content) => content.moduleId === module.id)
        ?? groupedContents.find((content) => content.moduleOrder === module.order)
        ?? groupedContents.find((content) => content.moduleOrder === index);
      modulesArray.push(this.createModuleFormFromCourse(module, index, moduleContents));
    });
  }

  private createModuleFormFromCourse(
    module: Module,
    index: number,
    moduleContents?: Content,
  ): FormGroup<ModuleForm> {
    const moduleForm = this.formBuilder.group<ModuleForm>({
      id: new FormControl(module.id, { nonNullable: true }),
      order: new FormControl(module.order ?? index, { nonNullable: true }),
      name: new FormControl(module.title, { nonNullable: true }),
      isActive: new FormControl((module as { isActive?: boolean }).isActive ?? true, { nonNullable: true }),
      classes: this.formBuilder.array<FormGroup<ClassForm>>([]),
      evaluations: this.formBuilder.array<FormGroup<EvaluationForm>>([]),
    });

    moduleContents?.classes.forEach((item, order) => {
      moduleForm.controls.classes.push(this.createClassFormFromCourse(item, order));
    });

    moduleContents?.evaluations.forEach((evaluation, order) => {
      moduleForm.controls.evaluations.push(this.createEvaluationFormFromCourse(evaluation, order));
    });

    return moduleForm;
  }

  private createClassFormFromCourse(item: Class, order: number): FormGroup<ClassForm> {
    return this.formBuilder.group<ClassForm>({
      id: new FormControl(item.id, { nonNullable: true }),
      order: new FormControl(item.order ?? order, { nonNullable: true }),
      name: new FormControl(item.title, { nonNullable: true }),
      type: new FormControl(item.type, { nonNullable: true }),
      videoTitle: new FormControl(item.videoTitle ?? '', { nonNullable: true }),
      videoThumbnailUrl: new FormControl(item.videoThumbnailUrl ?? '', { nonNullable: true }),
      videoFileName: new FormControl(item.videoFileName ?? '', { nonNullable: true }),
      editorContent: new FormControl(item.editorContent ?? '', { nonNullable: true }),
      youtubeUrl: new FormControl(item.youtubeUrl ?? '', { nonNullable: true }),
      vimeoUrl: new FormControl(item.vimeoUrl ?? '', { nonNullable: true }),
      webPageFileName: new FormControl(item.webPageFileName ?? '', { nonNullable: true }),
    });
  }

  private createEvaluationFormFromCourse(evaluation: Evaluation, order: number): FormGroup<EvaluationForm> {
    const evaluationForm = this.formBuilder.group<EvaluationForm>({
      id: new FormControl(evaluation.id, { nonNullable: true }),
      order: new FormControl(evaluation.order ?? order, { nonNullable: true }),
      questions: this.formBuilder.array<FormGroup<EvaluationQuestionForm>>([]),
    });

    evaluation.questions.forEach((question, questionOrder) => {
      evaluationForm.controls.questions.push(this.createEvaluationQuestionFormFromCourse(question, questionOrder));
    });

    return evaluationForm;
  }

  private createEvaluationQuestionFormFromCourse(
    question: EvaluationQuestion,
    questionOrder: number,
  ): FormGroup<EvaluationQuestionForm> {
    return this.formBuilder.group<EvaluationQuestionForm>({
      id: new FormControl(question.id, { nonNullable: true }),
      order: new FormControl(question.order ?? questionOrder, { nonNullable: true }),
      text: new FormControl(question.text, { nonNullable: true }),
      imageUrl: new FormControl(question.imageUrl ?? '', { nonNullable: true }),
      options: this.formBuilder.array<FormGroup<EvaluationOptionForm>>(
        question.options.map((option, optionOrder) => this.createEvaluationOptionFormFromCourse(option, optionOrder)),
      ),
    });
  }

  private createEvaluationOptionFormFromCourse(
    option: EvaluationOption,
    optionOrder: number,
  ): FormGroup<EvaluationOptionForm> {
    return this.formBuilder.group<EvaluationOptionForm>({
      id: new FormControl(option.id, { nonNullable: true }),
      order: new FormControl(option.order ?? optionOrder, { nonNullable: true }),
      text: new FormControl(option.text, { nonNullable: true }),
      isCorrect: new FormControl(option.isCorrect, { nonNullable: true }),
    });
  }

  private async saveCourse(status: CourseStatus, conclusionType: CourseConclusionTypesEnum): Promise<void> {
    if (this.isSaving) {
      return;
    }

    this.isSaving = true;

    const resolvedStatus = this.resolveSaveStatus(status);
    const resolvedConclusionType = this.resolveConclusionType(status, resolvedStatus, conclusionType);

    try {
      await this.uploadPendingAssets();

      const { about, modules } = this.formGroup.getRawValue();
      const coverImageUrl = this.resolvePersistableCoverImageUrl();

      const payload: CoursePayload = toCourseApiPayload({
        title: about.title,
        categoryId: about.categoryId,
        detailedDescription: about.detailedDescription,
        shortDescription: about.shortDescription,
        tags: about.tags,
        durationHours: about.duration,
        coverImageUrl,
        type: about.type,
        location: about.location,
        status: resolvedStatus,
        modules: modules.map((module, order) => this.buildModuleWritePayload(module, order)),
      });
      const related = toCourseRelatedResources({
        durationHours: about.duration,
        tags: about.tags,
        status: resolvedStatus,
        modules: modules.map((module, order) => ({
          order,
          title: module.name.trim(),
          ...(module.isActive === false ? { isActive: false } : {}),
        })),
        contents: modules.map((module, moduleOrder) => this.buildModuleContentPayload(module, moduleOrder)),
      });

      let saved: Course;

      if (this.isUpdate && this.courseId) {
        saved = await this.courseService.update(this.courseId, payload, related);
      } else {
        saved = await this.courseService.create(payload, related);
        this.courseId = saved.id;
        this.isUpdate = true;
      }

      this.applyCourse(saved);
      this.openConclusionModal(resolvedConclusionType);
    } catch (error) {
      this.toastService.error(getCrudErrorMessage(error), 'Oops...');
    } finally {
      this.isSaving = false;
    }
  }

  private resolveSaveStatus(requestedStatus: CourseStatus): CourseStatus {
    if (requestedStatus === CourseStatus.ACTIVE && !this.isCourseComplete) {
      return CourseStatus.DRAFT;
    }

    return requestedStatus;
  }

  private resolveConclusionType(
    requestedStatus: CourseStatus,
    resolvedStatus: CourseStatus,
    conclusionType: CourseConclusionTypesEnum,
  ): CourseConclusionTypesEnum {
    if (requestedStatus === CourseStatus.ACTIVE && resolvedStatus === CourseStatus.DRAFT) {
      return CourseConclusionTypesEnum.DRAFT;
    }

    return conclusionType;
  }

  private async uploadPendingAssets(): Promise<void> {
    await this.uploadPendingCoverImage();

    for (const moduleForm of this.modulesForm.controls) {
      for (const classForm of moduleForm.controls.classes.controls) {
        await this.uploadPendingClassAssets(classForm);
      }
    }
  }

  private async uploadPendingCoverImage(): Promise<void> {
    if (!this.coverImageFile) {
      return;
    }

    const publicUrl = await this.uploadService.uploadPublicImage(this.coverImageFile);
    this.aboutForm.controls.coverImageUrl.setValue(publicUrl);
    this.coverImageSrc = publicUrl;
    this.coverImageFile = null;
  }

  private async uploadPendingClassAssets(classForm: FormGroup<ClassForm>): Promise<void> {
    const classId = classForm.controls.id.value;
    const state = this.contentPanelUploadStates.get(classId);

    if (!state) {
      return;
    }

    await this.uploadPendingVideo(classForm, classId, state);
    await this.uploadPendingVideoThumbnail(classForm, classId);
    await this.uploadPendingWebPage(classForm, classId);
  }

  private async uploadPendingVideo(
    classForm: FormGroup<ClassForm>,
    classId: string,
    state: ClassContentUploadState,
  ): Promise<void> {
    if (!state.video?.file || classForm.controls.type.value !== ContentType.VIDEO) {
      return;
    }

    const publicUrl = await this.uploadService.uploadPublicFile(state.video.file, 'videos');
    classForm.controls.videoFileName.setValue(publicUrl);
    this.contentPanelUploadStates.set(classId, {
      ...this.contentPanelUploadStates.get(classId),
      video: { file: null, fileSizeLabel: state.video.fileSizeLabel },
    });
  }

  private async uploadPendingVideoThumbnail(
    classForm: FormGroup<ClassForm>,
    classId: string,
  ): Promise<void> {
    const state = this.contentPanelUploadStates.get(classId);

    if (!state?.thumbnail?.file || classForm.controls.type.value !== ContentType.VIDEO) {
      return;
    }

    const publicUrl = await this.uploadService.uploadPublicImage(state.thumbnail.file);
    classForm.controls.videoThumbnailUrl.setValue(publicUrl);
    this.contentPanelUploadStates.set(classId, {
      ...this.contentPanelUploadStates.get(classId),
      thumbnail: { file: null },
    });
  }

  private async uploadPendingWebPage(
    classForm: FormGroup<ClassForm>,
    classId: string,
  ): Promise<void> {
    const state = this.contentPanelUploadStates.get(classId);

    if (!state?.webPage?.file || classForm.controls.type.value !== ContentType.HTML) {
      return;
    }

    const publicUrl = await this.uploadService.uploadPublicFile(state.webPage.file, 'documents');
    classForm.controls.webPageFileName.setValue(publicUrl);
    this.contentPanelUploadStates.set(classId, {
      ...state,
      webPage: { file: null, fileSizeLabel: state.webPage.fileSizeLabel },
    });
  }

  private resolvePersistableCoverImageUrl(): string | undefined {
    const candidates = [this.coverImageSrc, this.aboutForm.controls.coverImageUrl.value];

    for (const candidate of candidates) {
      const trimmed = candidate.trim();

      if (trimmed && !trimmed.startsWith('blob:')) {
        return trimmed;
      }
    }

    return undefined;
  }

  private openConclusionModal(conclusionType: CourseConclusionTypesEnum): void {
    const content = COURSE_CONCLUSION_CONTENT[conclusionType];

    openSuccessModal(
      this.modalService,
      '560px',
      {
        title: content.title,
        subtitle: content.subtitle,
        iconPath: content.iconPath,
        imageAlt: content.title,
      },
      () => {
        void this.router.navigate(['/courses']);
      },
    );
  }

  private buildModuleWritePayload(
    module: {
      id: string;
      name: string;
      isActive: boolean;
      classes: ClassFormValue[];
      evaluations: {
        id: string;
        order: number;
        questions: {
          id: string;
          order: number;
          text: string;
          imageUrl: string;
          options: { id: string; order: number; text: string; isCorrect: boolean }[];
        }[];
      }[];
    },
    order: number,
  ): CourseModuleWritePayload {
    return {
      ...(this.persistedIds.has(module.id) ? { id: module.id } : {}),
      title: module.name.trim(),
      order,
      ...(module.isActive === false ? { isActive: false } : {}),
      contents: [
        ...module.classes
          .filter((courseClass) => courseClass.type !== null && courseClass.name.trim().length > 0)
          .map((courseClass) => toClassWritePayload(courseClass, this.persistedIds)),
        ...this.buildEvaluationWritePayloads(module.evaluations, module.classes.length),
      ],
    };
  }

  private buildEvaluationWritePayloads(
    evaluations: {
      id: string;
      order: number;
      questions: {
        id: string;
        order: number;
        text: string;
        imageUrl: string;
        options: { id: string; order: number; text: string; isCorrect: boolean }[];
      }[];
    }[],
    orderOffset: number,
  ): CourseContentWritePayload[] {
    return evaluations
      .filter((evaluation) => evaluation.questions.length > 0)
      .map((evaluation, index) => ({
        ...(this.persistedIds.has(evaluation.id) ? { id: evaluation.id } : {}),
        title: evaluation.questions[0]?.text.trim() || 'Avaliação',
        order: orderOffset + index,
        type: ContentType.ACTIVITY,
        questions: evaluation.questions.map((question) => ({
          ...(this.persistedIds.has(question.id) ? { id: question.id } : {}),
          question: question.text.trim(),
          ...(question.imageUrl.trim() ? { imageUrl: question.imageUrl.trim() } : {}),
          options: question.options.map((option) => ({
            ...(this.persistedIds.has(option.id) ? { id: option.id } : {}),
            text: option.text.trim(),
            isCorrect: option.isCorrect,
          })),
        })),
      }));
  }

  private buildModuleContentPayload(
    module: {
      classes: ClassFormValue[];
      evaluations: {
        order: number;
        questions: {
          order: number;
          text: string;
          imageUrl: string;
          options: { order: number; text: string; isCorrect: boolean }[];
        }[];
      }[];
    },
    moduleOrder: number,
  ): ContentPayload {
    return {
      moduleOrder,
      classes: module.classes
        .filter((courseClass) => courseClass.type !== null && courseClass.name.trim().length > 0)
        .map((courseClass) => this.buildClassPayload(courseClass)),
      evaluations: module.evaluations
        .filter((evaluation) => evaluation.questions.length > 0)
        .map((evaluation) => ({
          order: evaluation.order,
          questions: evaluation.questions.map((question) => ({
            order: question.order,
            text: question.text.trim(),
            imageUrl: question.imageUrl.trim() || undefined,
            options: question.options.map((option) => ({
              order: option.order,
              text: option.text.trim(),
              isCorrect: option.isCorrect,
            })),
          })),
        })),
    };
  }

  private buildClassPayload(courseClass: ClassFormValue): ClassPayload {
    const classPayload: ClassPayload = {
      order: courseClass.order,
      title: courseClass.name.trim(),
      type: courseClass.type!,
    };

    if (courseClass.videoTitle.trim()) {
      classPayload.videoTitle = courseClass.videoTitle.trim();
    }

    if (courseClass.videoThumbnailUrl.trim()) {
      classPayload.videoThumbnailUrl = courseClass.videoThumbnailUrl.trim();
    }

    if (courseClass.videoFileName.trim()) {
      classPayload.videoFileName = courseClass.videoFileName.trim();
    }

    if (courseClass.editorContent) {
      classPayload.editorContent = courseClass.editorContent;
    }

    if (courseClass.youtubeUrl.trim()) {
      classPayload.youtubeUrl = courseClass.youtubeUrl.trim();
    }

    if (courseClass.vimeoUrl.trim()) {
      classPayload.vimeoUrl = courseClass.vimeoUrl.trim();
    }

    if (courseClass.webPageFileName.trim()) {
      classPayload.webPageFileName = courseClass.webPageFileName.trim();
    }

    return classPayload;
  }

  private ensureValidActiveStep(): void {
    if (this.activeStepId === CourseCreateStepEnum.SUMMARY && !this.canAccessSummaryStep) {
      this.activeStepId = CourseCreateStepEnum.CONTENT;
    }
  }

  private isClassFilled(classValue: ClassFormValue): boolean {
    if (!classValue.type) {
      return false;
    }

    switch (classValue.type) {
      case ContentType.VIDEO:
        return !!(
          classValue.videoFileName.trim()
          || classValue.videoTitle.trim()
          || classValue.videoThumbnailUrl.trim()
        );
      case ContentType.RICH_TEXT:
        return hasVisibleRichTextContent(classValue.editorContent);
      case ContentType.YOUTUBE:
        return classValue.youtubeUrl.trim().length > 0;
      case ContentType.VIMEO:
        return classValue.vimeoUrl.trim().length > 0;
      case ContentType.HTML:
        return classValue.webPageFileName.trim().length > 0;
      default:
        return false;
    }
  }

  //#endregion

}
